import type { OutcomeReason } from '../../../types/encounterWork';

/**
 * Grouping for Claims to fix (GDN-3383).
 *
 * A denial queue is not a list of unrelated claims. The same authorization
 * rule or the same subscriber-id mistake produces a dozen of them, and the
 * fix is one decision applied a dozen times. Grouping is what turns that
 * pile into the shape of the work.
 *
 * Two rules this module holds to:
 *
 * - It is a RENDERING choice over the rows already loaded, never a query.
 *   The server sorts and pages; this only reorders what arrived and says
 *   where each run starts. So the sort inside a group is still the view's
 *   sort, untouched.
 * - It never invents a taxonomy. The category is the server's own
 *   `outcome_reason.category` and the payer is the payer's own name; when
 *   neither exists the rows go to a named "not recorded" bucket at the end
 *   rather than being silently folded into a neighbour.
 */

export type ClaimRowGroupBy = 'none' | 'reason' | 'payer';

export const CLAIM_ROW_GROUP_BY_KEYS: readonly ClaimRowGroupBy[] = [
  'none',
  'reason',
  'payer',
] as const;

/** Menu copy. `reason` says "category" because that is the field it reads. */
export const CLAIM_ROW_GROUP_MENU_LABELS: Record<ClaimRowGroupBy, string> = {
  none: 'None',
  payer: 'Payer',
  reason: 'Reason category',
};

/** Trigger copy, kept short so the toolbar button does not wrap. */
export const CLAIM_ROW_GROUP_TRIGGER_LABELS: Record<ClaimRowGroupBy, string> = {
  none: 'None',
  payer: 'Payer',
  reason: 'Reason',
};

export function isClaimRowGroupBy(value: string | null | undefined): value is ClaimRowGroupBy {
  return value != null && (CLAIM_ROW_GROUP_BY_KEYS as readonly string[]).includes(value);
}

/** The only row fields grouping reads. Kept narrow so it can be tested with
 *  plain objects instead of a whole claim work item. */
export interface ClaimRowGroupingFields {
  outcome_reason?: OutcomeReason | null;
  payer_name?: string | null;
}

export interface ClaimRowGroup<TRow> {
  /** Stable identity for the group, unique within one grouping. */
  key: string;
  /** Header text without the count, e.g. "Authorization required · CO-197". */
  label: string;
  count: number;
  rows: TRow[];
}

/** Rows the server could not place. Always last, whatever its size. */
const UNPLACED_KEY = '__unplaced__';

const UNPLACED_LABEL: Record<Exclude<ClaimRowGroupBy, 'none'>, string> = {
  payer: 'No payer recorded',
  reason: 'No reason recorded',
};

const trimmed = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text ? text : null;
};

interface GroupSeed {
  key: string;
  name: string;
  /** The row's leading reason code, when it has one. Decorates the header
   *  only while the whole group agrees on it. */
  code: string | null;
}

const seedFor = (
  fields: ClaimRowGroupingFields,
  groupBy: Exclude<ClaimRowGroupBy, 'none'>,
): GroupSeed | null => {
  if (groupBy === 'payer') {
    const payer = trimmed(fields.payer_name);
    return payer ? { code: null, key: payer, name: payer } : null;
  }
  const category = trimmed(fields.outcome_reason?.category);
  /* The code a payer call and an appeal letter both quote, and the one the
     Reason column already leads with. */
  const code = trimmed(fields.outcome_reason?.codes?.[0]);
  if (category) return { code, key: category, name: category };
  /* No normalized category: the code becomes the group's NAME, so it must
     not also decorate it. "CO-45 · CO-45" says nothing twice. */
  return code ? { code: null, key: code, name: code } : null;
};

interface Accumulator<TRow> {
  seed: GroupSeed;
  rows: TRow[];
  /** Null once two rows in this group disagree about the leading code. */
  code: string | null;
  codeAgreed: boolean;
}

/**
 * Order the loaded rows into groups.
 *
 * Groups come back biggest first, ties broken by where the group first
 * appears in the view's own order. Biggest first because the reason that
 * denied nine claims is the one worth a phone call, and a queue grouped by
 * reason is being asked exactly that question. Within a group the rows keep
 * the order they arrived in, which is the view's sort.
 *
 * `none` returns an empty array: there is nothing to group, and the caller
 * renders its rows exactly as before.
 */
export function groupClaimRows<TRow>(
  rows: readonly TRow[],
  groupBy: ClaimRowGroupBy,
  fieldsOf: (row: TRow) => ClaimRowGroupingFields,
): ClaimRowGroup<TRow>[] {
  if (groupBy === 'none') return [];

  const accumulators = new Map<string, Accumulator<TRow>>();

  for (const row of rows) {
    const seed = seedFor(fieldsOf(row), groupBy) ?? {
      code: null,
      key: UNPLACED_KEY,
      name: UNPLACED_LABEL[groupBy],
    };
    const existing = accumulators.get(seed.key);
    if (!existing) {
      accumulators.set(seed.key, {
        code: seed.code,
        codeAgreed: seed.code != null,
        rows: [row],
        seed,
      });
      continue;
    }
    existing.rows.push(row);
    if (existing.codeAgreed && existing.code !== seed.code) {
      existing.codeAgreed = false;
      existing.code = null;
    }
  }

  return (
    [...accumulators.values()]
      .map(entry => ({
        count: entry.rows.length,
        key: entry.seed.key,
        /* The code rides along only while every row in the group carries the
           same one. A header that named one code for a group holding two
           would be a claim about the rows that is not true of all of them. */
        label:
          entry.codeAgreed && entry.code ? `${entry.seed.name} · ${entry.code}` : entry.seed.name,
        rows: entry.rows,
      }))
      /* Stable sort, so equal counts keep first-appearance order. The
         unplaced bucket sinks regardless of size: "we do not know" is never
         the headline of a work queue. */
      .sort((left, right) => {
        if (left.key === UNPLACED_KEY) return 1;
        if (right.key === UNPLACED_KEY) return -1;
        return right.count - left.count;
      })
  );
}

/** The header row's text: the group's label with how many rows it holds. */
export function claimRowGroupHeader<TRow>(group: ClaimRowGroup<TRow>): string {
  return `${group.label} (${group.count})`;
}
